import { Box, Skeleton, useTheme } from '@mui/material';
import ImageNotSupported from '@mui/icons-material/ImageNotSupported';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { Section } from '../../ui/Section';

export interface PictureTileProps {
  src?: string | null;
  alt: string;
  fallback?: ReactNode;
  fallbackLabel?: string;
  loading?: boolean;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
  onImageError?: () => void;
  onImageLoad?: () => void;
  sx?: SxProps<Theme>;
}

type ImageState = 'loading' | 'loaded' | 'error';

export const PictureTile = ({
  src,
  alt,
  fallback,
  fallbackLabel,
  loading = false,
  objectFit = 'contain',
  objectPosition = 'center',
  onImageError,
  onImageLoad,
  sx,
}: PictureTileProps) => {
  const theme = useTheme();
  const hasImageSource = typeof src === 'string' && src.trim().length > 0;
  const [imageState, setImageState] = useState<ImageState>(hasImageSource ? 'loading' : 'error');

  useEffect(() => {
    setImageState(hasImageSource ? 'loading' : 'error');
  }, [hasImageSource, src]);

  const isImageLoading = loading || (hasImageSource && imageState === 'loading');
  const shouldRenderImage = hasImageSource && imageState !== 'error';

  return (
    <Box sx={sx}>
      <Section style={{ p: 0, mb: 0, height: '100%' }}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            minHeight: { xs: 220, sm: 260 },
            overflow: 'hidden',
            backgroundColor: theme.palette.custom.gray[50],
          }}
        >
          {shouldRenderImage && (
            <Box
              component="img"
              src={src ?? undefined}
              alt={alt}
              onLoad={() => {
                setImageState('loaded');
                onImageLoad?.();
              }}
              onError={() => {
                setImageState('error');
                onImageError?.();
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit,
                objectPosition,
                opacity: isImageLoading ? 0 : 1,
                transition: 'opacity 150ms ease-in-out',
              }}
            />
          )}

          {isImageLoading && (
            <Skeleton
              variant="rectangular"
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                bgcolor: theme.palette.custom.gray[100],
              }}
            />
          )}

          {!isImageLoading && !shouldRenderImage && (
            <Box
              role={fallbackLabel ? 'img' : undefined}
              aria-label={fallbackLabel}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                minHeight: { xs: 220, sm: 260 },
                color: theme.palette.custom.gray[500],
              }}
            >
              {fallback ?? (
                <ImageNotSupported aria-hidden="true" />
              )}
            </Box>
          )}
        </Box>
      </Section>
    </Box>
  );
};
